// God → Devices: every notification-capture phone across merchants, with app version,
// last-seen health and revoke.
import { useCallback, useEffect, useState } from "react";
import { BellOff, RefreshCw, Smartphone, Trash2, Wifi } from "lucide-react";
import { toast } from "sonner";
import { god } from "../godApi";
import { usePagedList } from "../../components/sc/ui";
import {
  Button, Callout, Cell, Dialog, Empty, Grid, Health, Kpi, Page, Pagination, Pill, Section, Table, Toolbar, ago, shortId, useNav, when,
} from "../kit";

const HOUR = 3600 * 1000;
const fresh = (iso) => !!iso && Date.now() - new Date(iso).getTime() < HOUR;

export default function Devices() {
  const { go } = useNav();
  const list = usePagedList((p) => god.devices(p), {}, 25);
  const [names, setNames] = useState({}); // merchant id → name
  const [vr, setVr] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [revoke, setRevoke] = useState(null);
  const { load } = list;

  const loadMeta = useCallback(() => {
    god.versions().then(setVr).catch(() => {});
    god.deviceGaps(24).then(setGaps).catch(() => {});
    god.notificationFacets()
      .then((f) => setNames(Object.fromEntries((f.merchants || []).map((m) => [m.id, m.name]))))
      .catch(() => {});
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  const refresh = () => { load(); loadMeta(); };

  const released = vr?.mobile_released;
  const fleet = vr?.mobile_in_field || [];
  const outdated = fleet.filter((m) => m.version !== released).reduce((a, m) => a + m.devices, 0);
  const onPage = list.data.items || [];
  const seenNow = onPage.filter((d) => fresh(d.last_seen_at)).length;

  const cols = [
    {
      key: "device", label: "Device", render: (d) => (
        <span className="dv-dev">
          <span className="dv-icon"><Smartphone size={15} /></span>
          <Cell main={d.device_name || "Unnamed device"} sub={d.platform || null} />
        </span>
      ),
    },
    { key: "merchant", label: "Merchant", render: (d) => <Cell main={names[d.merchant_id] || shortId(d.merchant_id)} sub={names[d.merchant_id] ? shortId(d.merchant_id) : null} /> },
    {
      key: "ver", label: "App version", render: (d) => (
        <span className="dv-ver">
          <span className="k-mono">{d.app_version || "—"}</span>
          {released && d.app_version && (d.app_version === released ? <Pill tone="green">current</Pill> : <Pill tone="amber">update</Pill>)}
        </span>
      ),
    },
    { key: "seen", label: "Last seen", render: (d) => (d.last_seen_at ? <span title={when(d.last_seen_at, true)}><Health ok={fresh(d.last_seen_at)} label={ago(d.last_seen_at)} /></span> : <span className="k-faint">Never</span>) },
    { key: "created", label: "Paired", hideSm: true, render: (d) => <Cell main={when(d.created_at)} sub={ago(d.created_at)} /> },
    {
      key: "actions", label: "Actions", align: "right", render: (d) => (
        <Button size="sm" variant="danger" icon={Trash2} onClick={() => setRevoke(d)}>Revoke</Button>
      ),
    },
  ];

  return (
    <Page>
      <Grid cols={4}>
        <Kpi hero icon={Smartphone} label="Devices" value={(list.data.total || 0).toLocaleString("en-IN")} sub="paired capture phones" />
        <Kpi icon={Wifi} label="Seen in the last hour" value={seenNow} sub={`of ${onPage.length} on this page`} />
        <Kpi icon={BellOff} label="Silent for 24h" value={gaps ? gaps.count : "—"} sub="no captured credit" onClick={() => go("notifications")} />
        <Kpi icon={RefreshCw} label="Released app" value={released || "—"} sub={vr ? (outdated ? `${outdated} on an old version` : "all up to date") : null} />
      </Grid>

      <Section title="Connected phones" sub="Most recently seen first. Green means seen in the last hour." icon={Smartphone} pad={false}>
        <Toolbar>
          <Button size="sm" icon={RefreshCw} onClick={refresh} disabled={list.loading}>Refresh</Button>
        </Toolbar>
        {list.error && <div className="dv-pad"><Callout tone="red" title="Couldn't load devices">{list.error}</Callout></div>}
        <Table testId="god-devices" columns={cols} rows={onPage} loading={list.loading}
          empty={<Empty icon={Smartphone} title="No devices connected">Devices appear once a merchant pairs the capture app.</Empty>} />
        <Pagination page={list.page} pages={list.data.pages || Math.max(1, Math.ceil((list.data.total || 0) / list.size))}
          total={list.data.total} size={list.size} onPage={list.setPage} onSize={(s) => { list.setSize(s); list.setPage(1); }} />
      </Section>

      {revoke && <RevokeDialog device={revoke} merchant={names[revoke.merchant_id]} onClose={() => setRevoke(null)} onDone={() => { setRevoke(null); refresh(); }} />}

      <style>{`
        .dv-dev { display: inline-flex; align-items: center; gap: 10px; min-width: 0; }
        .dv-icon { width: 30px; height: 30px; flex: none; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; background: #F2F3FA; color: #4b5263; }
        .dv-ver { display: inline-flex; align-items: center; gap: 8px; }
        .dv-pad { padding: 12px 18px 0; }
        .dv-p { margin: 0; font-size: 14px; line-height: 1.5; color: #374151; }
        @media (max-width: 760px) { .dv-pad { padding: 12px 14px 0; } }
      `}</style>
    </Page>
  );
}

function RevokeDialog({ device, merchant, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await god.revokeDevice(device.id); toast.success("Device revoked"); onDone(); }
    catch (x) { toast.error(x.message); setBusy(false); }
  };
  return (
    <Dialog title="Revoke this device?" onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="danger" onClick={run} disabled={busy}>{busy ? "Revoking…" : "Revoke"}</Button>
      </>}>
      <p className="dv-p">
        <b>{device.device_name || "This device"}</b>{merchant ? <> ({merchant})</> : null} is removed and stops sending
        notifications. The merchant has to pair the app again to use it.
      </p>
    </Dialog>
  );
}
